import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Avatar,
    Chip,
    Divider,
    IconButton,
    Collapse,
    Alert
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import ConfirmDialog from '../common/ConfirmDialog';
import api from '../../services/api';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

interface Answer {
    _id: string;
    userId: {
        _id: string;
        firstName: string;
        lastName: string;
        role: string;
    };
    content: string;
    createdAt: string;
}

interface Question {
    _id: string;
    userId: {
        _id: string;
        firstName: string;
        lastName: string;
        role: string;
    };
    title: string;
    content: string;
    answers: Answer[];
    isResolved: boolean;
    createdAt: string;
}

interface LessonQAProps {
    courseId: string;
    lessonId: string;
    courseName: string;
    lessonName: string;
}

const LessonQA: React.FC<LessonQAProps> = ({ courseId, lessonId, courseName, lessonName }) => {
    const { showToast } = useToast();
    const { user: authUser } = useAuth();
    const [questions, setQuestions] = useState<Question[]>([]);
    const [loading, setLoading] = useState(true);
    const [showNewQuestion, setShowNewQuestion] = useState(false);
    const [newQuestionTitle, setNewQuestionTitle] = useState('');
    const [newQuestionContent, setNewQuestionContent] = useState('');
    const [replyContent, setReplyContent] = useState('');
    const [activeQuestionId, setActiveQuestionId] = useState<string | null>(null);
    // showToast is already declared above
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [questionToDelete, setQuestionToDelete] = useState<string | null>(null);
    const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());
    const [answerInputs, setAnswerInputs] = useState<Map<string, string>>(new Map());

    useEffect(() => {
        fetchQuestions();
    }, [lessonId]);

    const fetchQuestions = async () => {
        try {
            const res = await api.get(`/api/questions/lesson/${lessonId}`);
            setQuestions(res.data);
        } catch (err) {
            console.error("Error fetching questions", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitQuestion = async () => {
        if (!newQuestionTitle.trim() || !newQuestionContent.trim()) {
            showToast("Veuillez remplir tous les champs", "error");
            return;
        }

        try {
            await api.post('/api/questions', {
                courseId,
                lessonId,
                title: newQuestionTitle,
                content: newQuestionContent
            });

            showToast("Question posée avec succès", "success");
            setNewQuestionTitle('');
            setNewQuestionContent('');
            setShowNewQuestion(false);
            fetchQuestions();
        } catch (err) {
            console.error("Error submitting question", err);
            showToast("Erreur lors de l'envoi de la question", "error");
        }
    };

    const handleSubmitAnswer = async (questionId: string) => {
        const content = answerInputs.get(questionId);
        if (!content?.trim()) {
            showToast("Veuillez saisir une réponse", "error");
            return;
        }

        try {
            await api.post(`/api/questions/${questionId}/answer`, { content });
            showToast("Réponse ajoutée", "success");
            setAnswerInputs(new Map(answerInputs.set(questionId, '')));
            fetchQuestions();
        } catch (err) {
            console.error("Error submitting answer", err);
            showToast("Erreur lors de l'envoi de la réponse", "error");
        }
    };

    const handleToggleResolved = async (questionId: string) => {
        try {
            await api.patch(`/api/questions/${questionId}/resolve`);
            fetchQuestions();
        } catch (err) {
            console.error("Error toggling resolved status", err);
            showToast("Erreur lors de la mise à jour", "error");
        }
    };

    const handleDeleteQuestion = async (questionId: string) => {
        setQuestionToDelete(questionId);
        setConfirmOpen(true);
    };

    const confirmDeleteQuestion = async () => {
        if (!questionToDelete) return;
        try {
            await api.delete(`/api/questions/${questionToDelete}`);
            setQuestions(questions.filter(q => q._id !== questionToDelete));
            if (activeQuestionId === questionToDelete) {
                setActiveQuestionId(null);
            }
            showToast('Question supprimée', 'success');
        } catch (err) {
            console.error("Error deleting question", err);
            showToast('Erreur lors de la suppression', 'error');
        } finally {
            setConfirmOpen(false);
            setQuestionToDelete(null);
        }
    };

    const toggleQuestion = (questionId: string) => {
        const newExpanded = new Set(expandedQuestions);
        if (newExpanded.has(questionId)) {
            newExpanded.delete(questionId);
        } else {
            newExpanded.add(questionId);
        }
        setExpandedQuestions(newExpanded);
    };

    const getRoleBadgeColor = (role: string) => {
        switch (role) {
            case 'super-admin': return 'error';
            case 'of-admin': return 'warning';
            case 'trainer': return 'info';
            default: return 'default';
        }
    };

    if (loading) {
        return <Box sx={{ p: 2 }}>Chargement...</Box>;
    }

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <QuestionAnswerIcon color="primary" />
                    <Typography variant="h6" sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 600 }}>
                        Questions & Réponses
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    size="small"
                    onClick={() => setShowNewQuestion(!showNewQuestion)}
                    sx={{ fontFamily: 'Quicksand, sans-serif', textTransform: 'none' }}
                >
                    {showNewQuestion ? 'Annuler' : 'Poser une question'}
                </Button>
            </Box>

            {showNewQuestion && (
                <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8fafc' }}>
                    <TextField
                        fullWidth
                        label="Titre de la question"
                        value={newQuestionTitle}
                        onChange={(e) => setNewQuestionTitle(e.target.value)}
                        sx={{ mb: 2 }}
                        InputProps={{ style: { fontFamily: 'Quicksand, sans-serif' } }}
                    />
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Votre question"
                        value={newQuestionContent}
                        onChange={(e) => setNewQuestionContent(e.target.value)}
                        sx={{ mb: 2 }}
                        InputProps={{ style: { fontFamily: 'Quicksand, sans-serif' } }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleSubmitQuestion}
                        sx={{ fontFamily: 'Quicksand, sans-serif', textTransform: 'none' }}
                    >
                        Envoyer la question
                    </Button>
                </Paper>
            )}

            {questions.length === 0 ? (
                <Alert severity="info" sx={{ fontFamily: 'Quicksand, sans-serif' }}>
                    Aucune question pour cette leçon. Soyez le premier à poser une question !
                </Alert>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {questions.map((question) => (
                        <Paper key={question._id} sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                                            {question.userId.firstName[0]}
                                        </Avatar>
                                        <Typography variant="body2" sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 600 }}>
                                            {question.userId.firstName} {question.userId.lastName}
                                        </Typography>
                                        <Chip
                                            label={question.userId.role}
                                            size="small"
                                            color={getRoleBadgeColor(question.userId.role)}
                                        />
                                        {question.isResolved && (
                                            <Chip
                                                icon={<CheckCircleIcon />}
                                                label="Résolu"
                                                size="small"
                                                color="success"
                                            />
                                        )}
                                    </Box>
                                    <Typography variant="subtitle1" sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 600, mb: 0.5 }}>
                                        {question.title}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'Quicksand, sans-serif', color: 'text.secondary' }}>
                                        {question.content}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    {['trainer', 'of-admin', 'super-admin'].includes(authUser?.role || '') && (
                                        <IconButton size="small" onClick={() => handleToggleResolved(question._id)}>
                                            <CheckCircleIcon color={question.isResolved ? 'success' : 'disabled'} />
                                        </IconButton>
                                    )}
                                    {(authUser?._id === question.userId._id || ['of-admin', 'super-admin'].includes(authUser?.role || '')) && (
                                        <IconButton size="small" onClick={() => handleDeleteQuestion(question._id)}>
                                            <DeleteIcon color="error" />
                                        </IconButton>
                                    )}
                                    <IconButton size="small" onClick={() => toggleQuestion(question._id)}>
                                        {expandedQuestions.has(question._id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                </Box>
                            </Box>

                            <Collapse in={expandedQuestions.has(question._id)}>
                                <Divider sx={{ my: 2 }} />
                                {question.answers.length > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                        {question.answers.map((answer) => (
                                            <Box key={answer._id} sx={{ display: 'flex', gap: 1, mb: 2, pl: 2, borderLeft: '3px solid #e0e0e0' }}>
                                                <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
                                                    {answer.userId.firstName[0]}
                                                </Avatar>
                                                <Box sx={{ flex: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                        <Typography variant="caption" sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 600 }}>
                                                            {answer.userId.firstName} {answer.userId.lastName}
                                                        </Typography>
                                                        <Chip
                                                            label={answer.userId.role}
                                                            size="small"
                                                            color={getRoleBadgeColor(answer.userId.role)}
                                                            sx={{ height: 18 }}
                                                        />
                                                    </Box>
                                                    <Typography variant="body2" sx={{ fontFamily: 'Quicksand, sans-serif' }}>
                                                        {answer.content}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Votre réponse..."
                                        value={answerInputs.get(question._id) || ''}
                                        onChange={(e) => setAnswerInputs(new Map(answerInputs.set(question._id, e.target.value)))}
                                        InputProps={{ style: { fontFamily: 'Quicksand, sans-serif' } }}
                                    />
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => handleSubmitAnswer(question._id)}
                                        sx={{ fontFamily: 'Quicksand, sans-serif', textTransform: 'none' }}
                                    >
                                        Répondre
                                    </Button>
                                </Box>
                            </Collapse>
                        </Paper>
                    ))}
                </Box>
            )}
            {/* Confirm Dialog */}
            <ConfirmDialog
                open={confirmOpen}
                title="Supprimer la question"
                content="Êtes-vous sûr de vouloir supprimer cette question ? Cette action est irréversible."
                onConfirm={confirmDeleteQuestion}
                onCancel={() => setConfirmOpen(false)}
                isDestructive
            />
        </Box>
    );
};

export default LessonQA;
